import { useEffect, useState } from 'react';

interface NumberFieldProps {
  label: string;
  value: number;
  onCommit: (value: number) => void;
  step?: number;
  min?: number;
  max?: number;
  suffix?: string;
  decimals?: number;
  disabled?: boolean;
}

export function NumberField({
  label,
  value,
  onCommit,
  step = 0.01,
  min,
  max,
  suffix,
  decimals = 3,
  disabled,
}: NumberFieldProps): JSX.Element {
  const [text, setText] = useState(value.toFixed(decimals));
  useEffect(() => setText(value.toFixed(decimals)), [value, decimals]);

  const commit = () => {
    const parsed = Number(text);
    if (!Number.isFinite(parsed)) {
      setText(value.toFixed(decimals));
      return;
    }
    const constrained = Math.min(max ?? Infinity, Math.max(min ?? -Infinity, parsed));
    setText(constrained.toFixed(decimals));
    if (Math.abs(constrained - value) > 1e-9) onCommit(constrained);
  };

  return (
    <label className="number-field">
      <span>{label}</span>
      <span className="number-input-wrap">
        <input
          type="number"
          value={text}
          step={step}
          min={min}
          max={max}
          disabled={disabled}
          onChange={(event) => setText(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              commit();
              event.currentTarget.blur();
            }
          }}
        />
        {suffix ? <small>{suffix}</small> : null}
      </span>
    </label>
  );
}
